import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone, FiFacebook, FiTwitter, FiInstagram, FiSend } from 'react-icons/fi';
import axios from 'axios';
import { generateSlug } from '../utils/slugify';
import { getImageUrl } from '../utils/imageUtils';
import './Footer.css';

const Footer = () => {
  const [recentProperties, setRecentProperties] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/properties?sort=date&limit=2`);
        setRecentProperties(response.data);
      } catch (err) {
        console.error('Error fetching recent properties for footer:', err);
      }
    };
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings`);
        setSettings(response.data);
      } catch (err) {
        console.error('Error fetching settings for footer:', err);
      }
    };
    fetchRecent();
    fetchSettings();
  }, []);

  return (
    <footer className="footer bg-slate-900 text-slate-300 pt-12 pb-6">
      <div className="container">
        <div className="grid-4 mb-12">
          {/* Col 1 */}
          <div className="col-span-1">
            <Link to="/" className="logo flex items-center gap-2 mb-4">
              <img 
                src="/icon-192.png" 
                alt="Real Properties Icon" 
                style={{ width: '32px', height: '32px', objectFit: 'contain' }} 
              />
              <span className="font-bold text-xl text-white">Real Properties</span>
            </Link>
            <p className="flex items-center gap-2 text-sm mb-2" style={{ color: '#9ca3af' }}><FiMail /> wealthassociates.com@gmail.com</p>
            <p className="flex items-center gap-2 text-sm mb-6" style={{ color: '#9ca3af' }}><FiPhone /> +91 77963 56789</p>
            <div className="flex gap-3" style={{ color: '#fff' }}>
              {settings?.socialLinks?.facebook && <a href={settings.socialLinks.facebook} target="_blank" rel="noreferrer" className="social-icon"><FiFacebook size={18} /></a>}
              {settings?.socialLinks?.twitter && <a href={settings.socialLinks.twitter} target="_blank" rel="noreferrer" className="social-icon"><FiTwitter size={18} /></a>}
              {settings?.socialLinks?.instagram && <a href={settings.socialLinks.instagram} target="_blank" rel="noreferrer" className="social-icon"><FiInstagram size={18} /></a>}
              {settings?.socialLinks?.linkedin && <a href={settings.socialLinks.linkedin} target="_blank" rel="noreferrer" className="social-icon"><FiSend size={18} /></a>}
              
              {/* Fallbacks if no settings are loaded yet */}
              {!settings && (
                <>
                  <Link to="#" className="social-icon"><FiFacebook size={18} /></Link>
                  <Link to="#" className="social-icon"><FiTwitter size={18} /></Link>
                  <Link to="#" className="social-icon"><FiInstagram size={18} /></Link>
                  <Link to="#" className="social-icon"><FiSend size={18} /></Link>
                </>
              )}
            </div>
          </div>

          {/* Col 2 */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Quick Links</h3>
            <ul className="footer-links">
              <li><Link to="/properties">Buy a property</Link></li>
              <li><Link to="/dashboard/add-property">Sell a property</Link></li>
              <li><Link to="/rent">Rent a property</Link></li>
            </ul>
          </div>

          {/* Col 3 */}
          <div>
            <h4 className="font-bold mb-6 text-white">About</h4>
            <ul className="footer-links">
              <li><Link to="/page/about-us">About Us</Link></li>
              <li><Link to="/page/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/page/terms-of-service">Terms of Service</Link></li>
              <li><Link to="/contact-us">Contact Us</Link></li>
            </ul>
          </div>

          {/* Col 4 */}
          <div>
            <h4 className="font-bold mb-6 text-white">Recent Posts</h4>
            <div className="flex flex-col gap-4">
              {recentProperties.map(prop => (
                <Link to={`/property/${generateSlug(prop.title)}/${prop._id}`} key={prop._id} className="flex gap-3 items-start group">
                  <div style={{ width: '60px', height: '60px', borderRadius: '8px', border: '1px solid #333', flexShrink: 0, overflow: 'hidden', backgroundColor: '#334155' }}>
                    <img 
                      src={prop.images && prop.images.length > 0 ? getImageUrl(prop.images[0]) : 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80'} 
                      alt={prop.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.src = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80'; }}
                    />
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm hover:text-primary transition-colors line-clamp-2" style={{ color: '#f8fafc' }}>{prop.title}</h5>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>{new Date(prop.createdAt).toLocaleDateString(undefined, {month: 'short', day: '2-digit'})}</p>
                  </div>
                </Link>
              ))}
              {recentProperties.length === 0 && (
                <p className="text-sm" style={{ color: '#9ca3af' }}>No recent properties.</p>
              )}
            </div>
          </div>
        </div>

        {/* Download App Banner */}
        <div 
          className="app-banner p-8 rounded-xl flex mb-8 text-white shadow-lg"
          style={{
            background: '#1f1f29',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '2rem'
          }}
        >
          <div className="flex items-center gap-6" style={{ flexWrap: 'wrap' }}>
            <div 
              style={{
                width: '80px', 
                height: '80px', 
                backgroundColor: '#fff', 
                borderRadius: '16px', 
                backgroundImage: 'url("/app-icon-downloaded.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexShrink: 0
              }}
            ></div>
            <div>
              <h3 className="font-bold text-2xl mb-2">Download Now</h3>
              <p className="text-sm opacity-80" style={{maxWidth: '400px'}}>Find everything you need for buying, selling & renting property in our new Wealth Associates App!</p>
            </div>
          </div>
          <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
            <a 
              href="https://apps.apple.com/in/app/wealth-associate/id6743356719" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ transition: 'transform 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                alt="Download on the App Store" 
                style={{ height: '48px' }}
              />
            </a>
            
            <a 
              href="https://play.google.com/store/apps/details?id=com.wealthassociates.alpha&hl=en_IN" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ transition: 'transform 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Get it on Google Play" 
                style={{ height: '48px' }}
              />
            </a>
          </div>
        </div>

        <div className="text-center text-sm mt-8 pt-12 pb-6 flex flex-col md:flex-row justify-center items-center gap-2" style={{ color: '#9ca3af', borderTop: '1px solid #334155' }}>
          <span>&copy; {new Date().getFullYear()} <strong className="text-white hover:text-primary transition-colors cursor-pointer">realproperties.in</strong>. All rights reserved.</span>
          <span>Developed by <a href="#" target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-primary transition-colors">Kriya IT Solutions</a></span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
